"use client";

import { SessionLoading } from "@/components/ui/session-loading";
import { useSession } from "@/context/SessionContext";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

interface SessionLoadingWrapperProps {
  children: React.ReactNode;
}

export const SessionLoadingWrapper: React.FC<SessionLoadingWrapperProps> = ({
  children,
}) => {
  const { isLoading, isRefreshing, isLoggingOut } = useSession();
  const pathname = usePathname() || "";
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    // Define public paths that don't require authentication (from middleware)
    const publicPaths = [
      "/signin",
      "/signup",
      "/forgot-password",
      "/reset-password",
      "/confirm",
      "/callback",
      "/candidates",
      "/customers",
      "/docs",
      "/services",
      "/info",
      "/monitoring",
      "/view",
      "/error",
      "/api",
      "/invalid",
      "/pricing",
      "/faq",
      "/features",
      "/cookies",
      "/dpa",
      "/privacy",
      "/terms",
      "/coming-soon",
      "/priority-club",
      "/thank-you",
    ];

    // Define transitional paths (not in signedIn, but may need session)
    const transitionalPaths = ["/create-company", "/invitations"];

    // Check if current path is public (including homepage)
    const isPublicPath =
      pathname === "" ||
      pathname === "/" ||
      publicPaths.some((p) => pathname.startsWith(p));

    // Check if current path is transitional
    const isTransitionalPath = transitionalPaths.includes(pathname || "");

    // Check if current path starts with candidate or customer routes
    const isExternalLink =
      pathname.startsWith("/candidates/") ||
      pathname.startsWith("/candidate/") ||
      pathname.startsWith("/customers/");

    // Only show loading for routes inside (signedIn) - routes that require authentication
    // This means: NOT public, NOT transitional, and NOT external links
    const isSignedInRoute =
      !isPublicPath && !isTransitionalPath && !isExternalLink;

    if (!isSignedInRoute) {
      setShowLoading(false);
      return;
    }

    // Only show loading for signed-in pages that require authentication
    // Add a small delay to prevent flashing
    const timer = setTimeout(() => {
      if (isLoading || isRefreshing || isLoggingOut) {
        setShowLoading(true);
      } else {
        setShowLoading(false);
      }
    }, 100); // 100ms delay to prevent flashing

    return () => clearTimeout(timer);
  }, [isLoading, isRefreshing, isLoggingOut, pathname]);

  if (showLoading) {
    let message = "Cargando sesión...";
    if (isLoggingOut) {
      message = "Cerrando sesión...";
    } else if (isRefreshing) {
      message = "Actualizando sesión...";
    }
    return <SessionLoading message={message} />;
  }

  return <>{children}</>;
};
